import React, { useState, useEffect, useRef } from 'react';
import { CaptureOverlay } from '../shared';
import { setUnsavedCapture } from '../shared/unsavedWork';
import { safeExitPiP } from '../../../../utils/mediaUtils';

interface GuidedUploadProps {
  taskName: string;
  onSubmit: (file: File, notes: string) => void;
  onSaveDraft: (file: File | null, notes: string) => void;
  onClose: () => void;
  /** Called on submit/close to clean up sessionStorage */
  onCleanup?: () => void;
  initialFile?: File;
  initialNotes?: string;
}

const ACCEPTED_VIDEO = 'video/*';
// Sanity ceiling only — the transcode pipeline (stage 2 of the media
// compression spec) shrinks anything plausible well below the 500 MB
// storage rule. This guard exists so a pathological pick fails kindly at
// selection instead of after minutes of upload.
const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} KB`;
  const mb = (bytes / (1024 * 1024)).toFixed(1);
  return `${mb} MB`;
}

export const GuidedUpload: React.FC<GuidedUploadProps> = ({
  taskName,
  onSubmit,
  onSaveDraft,
  onClose,
  onCleanup,
  initialFile,
  initialNotes,
}) => {
  const [file, setFile] = useState<File | null>(initialFile || null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(() =>
    initialFile ? URL.createObjectURL(initialFile) : null
  );
  const [notes, setNotes] = useState(initialNotes || '');

  /* Picked file not yet saved → page-level leave nudge */
  useEffect(() => {
    setUnsavedCapture(!!file);
    return () => setUnsavedCapture(false);
  }, [file]);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = () => {
    // Fix 6: exit PiP before opening file picker
    safeExitPiP();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_BYTES) {
      setSizeError(
        `That recording is ${formatSize(selected.size)}, which is larger than we can accept. If it was split into parts, try uploading one part.`
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSizeError(null);
    // Fix 6: exit PiP on file selection
    safeExitPiP();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // Use blob URL for preview. Do NOT use readAsDataURL — it creates a
    // base64 string 33% larger than the file, overflowing memory on Android.
    const url = URL.createObjectURL(selected);
    setFile(selected);
    setPreviewUrl(url);
  };

  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = () => {
    if (!file) return;
    // Fix 6: exit PiP on submit
    safeExitPiP();
    onCleanup?.();
    onSubmit(file, notes);
  };

  const handleClose = () => {
    safeExitPiP();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onCleanup?.();
    onClose();
  };

  const canSubmit = file !== null;

  return (
    <CaptureOverlay taskName={taskName} onClose={handleClose}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_VIDEO}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div className="gc-scroll-body">
        <div className="gc-section-heading">Upload your recording</div>

        {sizeError && (
          <p role="alert" style={{ color: 'var(--ember)', fontSize: '14px', lineHeight: 1.4, margin: '8px 0' }}>
            {sizeError}
          </p>
        )}

        {/* File preview card */}
        {file && previewUrl && (
          <div className="gc-upload-card">
            <div className="gc-upload-preview">
              <video src={previewUrl} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div className="gc-selected-badge">SELECTED</div>
            </div>
            <div className="gc-upload-info">
              <div>
                <div className="gc-upload-name">{file.name}</div>
                <div className="gc-upload-meta">{formatSize(file.size)}</div>
              </div>
              <button className="gc-upload-delete" onClick={handleRemove}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round">
                  <polyline points="3,6 5,6 21,6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Drop zone / pick another */}
        <div className="gc-drop-zone" onClick={handlePick}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          <div className="gc-drop-text">
            {file ? 'Tap to select a different recording' : 'Tap to select a recording'}
          </div>
        </div>

        {/* Notes */}
        <div className="co-note-wrap">
          <div className="co-note-label">Optional note</div>
          <textarea
            className="co-textarea"
            placeholder="Add context, reactions, or anything else…"
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value.slice(0, 2000))}
          />
          <div className="co-charcount">{notes.length} / 2000</div>
        </div>
      </div>

      <div className="gc-bottom-simple">
        <button
          className="gc-btn-cta"
          disabled={!canSubmit}
          style={canSubmit ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          onClick={handleSubmit}
        >
          Review &amp; Submit
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
        <button
          className="gc-btn-skip"
          onClick={() => { onCleanup?.(); onSaveDraft(file, notes); }}
        >
          Save Draft
        </button>
      </div>
    </CaptureOverlay>
  );
};
